import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, BookOpen, Calendar, CheckCheck, Trophy, Info, Bell } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { apiRequest } from "@/lib/queryClient";
import { capturePostHogEvent } from "@/lib/posthog";
import { ParentLayout } from "@/components/parent/ParentLayout";

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  ctaUrl: string | null;
  readAt: string | null;
  createdAt: string;
}

const ICONS: Record<string, any> = {
  dunning: AlertCircle,
  weekly_digest: BookOpen,
  class_reminder: Calendar,
  milestone: Trophy,
  system: Info,
};

export default function ParentNotifications() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ notifications: Notification[]; unreadCount: number }>({
    queryKey: ["/api/parent/notifications"],
  });

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/parent/notifications/${id}/read`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/parent/notifications"] }),
  });

  const markAll = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/parent/notifications/read-all");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/parent/notifications"] }),
  });

  return (
    <ParentLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">Notifications</h1>
            <p className="text-sm text-muted-foreground">Alerts, weekly digests, class reminders, and milestones.</p>
          </div>
          {data && data.unreadCount > 0 && (
            <Button variant="outline" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
              <CheckCheck className="w-4 h-4 mr-2" />
              Mark all read
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[0,1,2].map((i) => <Skeleton key={i} className="h-20" />)}
          </div>
        ) : !data || data.notifications.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center space-y-3">
              <Bell className="w-10 h-10 mx-auto text-muted-foreground" />
              <p className="font-semibold">No notifications yet</p>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Failed payments, weekly summaries, and upcoming class reminders will show up here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {data.notifications.map((n) => {
              const Icon = ICONS[n.type] ?? Info;
              return (
                <Card
                  key={n.id}
                  className={`cursor-pointer transition-colors ${n.readAt ? "" : "border-primary/40 bg-primary/5"}`}
                  onClick={() => {
                    capturePostHogEvent("parent_notification_clicked", { type: n.type, hadCta: !!n.ctaUrl });
                    if (!n.readAt) markRead.mutate(n.id);
                    if (n.ctaUrl) window.location.href = n.ctaUrl;
                  }}
                  data-testid={`notification-${n.id}`}
                >
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className={`shrink-0 mt-0.5 rounded-md p-2 ${n.type === "dunning" ? "bg-destructive/10 text-destructive" : "bg-muted text-foreground"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm">{n.title}</p>
                        {!n.readAt && <Badge variant="default" className="text-[10px] h-4 px-1.5">New</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">{n.body}</p>
                      <p className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </ParentLayout>
  );
}
